/* eslint-disable no-underscore-dangle */
import _ from "lodash";
import DateColumn from "../column-helpers/date/date-component";
import UptimeColumn from "../column-helpers/uptime/uptime-component";
import DeviceTypeColumn from "../column-helpers/device_type/device-type-component";
import ManagementStateColumn from "../column-helpers/management_state/management-state-component";
import DeviceLicenseStatusComponent from "../column-helpers/licence_status/device-license-status-component";
import DeviceDiscoveryStatus from "../column-helpers/discovery_status/device-discovery-status-component";
import AccessStatusColumn from "../column-helpers/access_status/access-status-component";
import ControlTypeColumn from "../column-helpers/control_type/ControlTypeComponent";
import SitePathColumn from "../column-helpers/site_path/site-path-component";
import DiscoveryColumn from "../column-helpers/discovery_level/discovery-level-component";
import AllowSiteAutoAssignmentColumn from "../column-helpers/allow_site_auto_assignment/AllowSiteAutoAssignmentComponent";
import CmdbSyncStatusColumn from "../column-helpers/cmdb_sync_status/CmdbSyncStatusComponent";
import BooleanColumn from "../column-helpers/boolean/boolean-component";
import ArrayColumn from "../column-helpers/array/array-component";
import EnvironmentComponent from "../column-helpers/environment/environment-component";
import DeviceCaptureStatusColumn from "../column-helpers/capture_status/device-capture-status-component";
import AuditStatusColumn from "../column-helpers/audit_status/audit-status-component";
import PlanStatusColumn from "../column-helpers/plan_status/plan-status-component";

import {
    DEVICE_DISCOVERY_STATUSES,
    DEVICE_MANAGEMENT_STATES,
    LICENSE_STATUS,
    ACCESS_STATUS_LABELS,
    DISRUPTIVE_STATUS,
    DEVICE_CAPTURE_STATUSES,
    AUDIT_STATUSES,
} from "../column-helpers/deviceConstants";

const Steve = require("@gluenetworks/steve_core").getInstance();
const PlatformSdk = require("@gluenetworks/platform-sdk-remote");

const DeviceEntity = PlatformSdk.entities.ws.Device;
const SiteEntity = PlatformSdk.entities.ws.Site;
const CredentialsEntity = PlatformSdk.entities.ws.Credentials;
const OsImageServerEntity = PlatformSdk.entities.ws.OsImageServer;

const CUSTOM_FIELD_VALUES_PREFIX = "customFieldValues.";
const CUSTOM_FIELD_UUID_PATTERN =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

const _isCustomFieldUuid = (value) => {
    return _.isString(value) && CUSTOM_FIELD_UUID_PATTERN.test(value);
};

const _buildNormalizedColumnKey = (columnKey) => {
    if (_.startsWith(columnKey, CUSTOM_FIELD_VALUES_PREFIX)) {
        return columnKey;
    }

    if (_isCustomFieldUuid(columnKey)) {
        return `${CUSTOM_FIELD_VALUES_PREFIX}${columnKey}`;
    }

    return columnKey;
};

const _normalizeD3ColumnConfig = (rawColumnConfig = {}) => {
    return Object.entries(rawColumnConfig).reduce((normalizedConfig, [key, columnConfig]) => {
        const normalizedKey = _buildNormalizedColumnKey(key);

        const normalizedColumnConfig = _.cloneDeep(columnConfig);
        if (_.startsWith(normalizedKey, CUSTOM_FIELD_VALUES_PREFIX)) {
            normalizedColumnConfig.path = normalizedKey;
        }

        normalizedConfig[normalizedKey] = normalizedColumnConfig;
        return normalizedConfig;
    }, {});
};

const getLegacyColumnConfig = async () => {
    try {
        const activeOrgId = _.get(Steve, "userSession.activeOrganization.id");
        const activeOrgName = _.get(Steve, "userSession.activeOrganization.name");

        // Remap device constants to match column config options format
        const getMappedDeviceConstant = (deviceConstant) => {
            const mappedObject = {};
            Object.values(deviceConstant).forEach((el) => {
                mappedObject[el.name] = {
                    title: el.title,
                    icon: el.dashboardIcon,
                };
            });

            return mappedObject;
        };

        // Option Type Base for detectedTypeBase column
        const typeBaseMap = await DeviceEntity.loadAndMergeTypeBaseMap(activeOrgId);
        const typeBaseList = _.sortBy(
            Object.keys(typeBaseMap).map((key) => {
                return {
                    value: typeBaseMap[key],
                    title: typeBaseMap[key],
                };
            }),
            "title"
        );

        // Option Environment for environment column
        const environmentLabels = DeviceEntity.getEnvironmentLabels();
        const environmentValues = DeviceEntity.getEnvironmentValues();
        const environmentKeys = Object.keys(environmentLabels);
        const environmentList = _.sortBy(
            environmentKeys.map((key) => {
                return {
                    value: environmentValues[key],
                    title: environmentLabels[key],
                };
            }),
            "title"
        );

        // Option Control Type for controlType column
        // The enum key is what the DB stores; the label is display only.
        const controlTypeLabels = DeviceEntity.getControlTypeLabels();
        const controlTypeList = _.sortBy(
            Object.entries(controlTypeLabels).map(([value, title]) => ({value, title})),
            "title"
        );

        // Option Site Type for sitePath column
        // The enum key is what the DB stores; the label is display only.
        const sitesTypeLabels = SiteEntity.getLocationTypeLabels();
        const sitesTypeList = _.sortBy(
            Object.entries(sitesTypeLabels).map(([value, title]) => ({value, title})),
            "title"
        );

        // Option Credentials for controllerName column
        const credentialsMap = await CredentialsEntity.loadLabelValues(activeOrgId, {
            $exists: true,
        });
        const credentialsList = _.sortBy(
            credentialsMap.map((credential) => {
                return {
                    value: credential.label,
                    title: credential.label,
                };
            }),
            "title"
        );

        // Option Vendor for vendor column
        const vendorMap = await DeviceEntity.loadAndMergeVendorMap(activeOrgId);
        const vendorList = _.sortBy(
            Object.keys(vendorMap).map((vendor) => {
                return {
                    value: vendorMap[vendor],
                    title: vendorMap[vendor],
                };
            }),
            "title"
        );

        // Backup Label Map for backupStatus column
        const backupLabelMap = DeviceEntity.getLastBackupStatusLabels();

        // Restore Label Map for restoreStatus column
        const restoreLabelMap = DeviceEntity.getLastRestoredStatusLabels();

        // Discovery Label Map for discovery column
        const discoveryMap = DeviceEntity.getDiscoveryLevelLabels();

        // CMDB Sync Status Map for cmdbSyncStatus column
        const cmdbSyncStatusMap = DeviceEntity.getCmdbSyncStatusLabels();
        const cmdbSyncStatusList = _.sortBy(
            Object.keys(cmdbSyncStatusMap).map((cmdbSyncStatus) => {
                return {
                    value: cmdbSyncStatus,
                    title: cmdbSyncStatusMap[cmdbSyncStatus],
                };
            }),
            "title"
        );

        // OS Map for detectedOs column
        const osMap = await DeviceEntity.loadAndMergeOsMap(activeOrgId);
        const osList = _.sortBy(
            Object.entries(osMap).map(([, title]) => ({value: title, title})),
            "title"
        );

        // Server Map for fileServerName column
        let serverList = [];
        if (Steve?.licenseRestriction?.checkLicense("OSM")) {
            const osImageServers = await OsImageServerEntity.loadAscendOsImageServers(
                activeOrgId,
                {},
                {
                    id: true,
                    name: true,
                },
                {
                    name: 1,
                }
            );

            serverList = _.sortBy(
                osImageServers.map((osImageServer) => {
                    return {
                        title: osImageServer.name,
                        value: osImageServer.name,
                    };
                }),
                "title"
            );
        }

        // Support function for osUpgradeStatus column
        const _getOsPlanDataForAdvancedFilter = (data) => {
            let clonedData = _.clone(data);
            let newObj = {};
            for (let key in clonedData) {
                let value = {};
                value[key] = {
                    name: key,
                    title: clonedData[key],
                };
                newObj = Object.assign(newObj, value);
            }
            return newObj;
        };

        const legacyDeviceColumnConfig = {
            detectedTypeBase: {
                label: "Type",
                dataType: "enum",
                entityColumn: true,
                columnRendererComponent: DeviceTypeColumn,
                options: typeBaseList,
                width: 65,
                shown: true,
                priority: 1,
                groupable: true,
            },
            name: {
                label: "Name",
                dataType: "text",
                width: 220,
                shown: true,
                priority: 2,
                groupable: false,
            },
            "connectionInformation.ip": {
                label: "IP Address",
                dataType: "text",
                path: "connectionInformation.ip",
                width: 140,
                shown: true,
                priority: 3,
                groupable: false,
            },
            discoveryStatus: {
                label: "Discovered Status",
                dataType: "enum",
                options: getMappedDeviceConstant(DEVICE_DISCOVERY_STATUSES),
                columnRendererComponent: DeviceDiscoveryStatus,
                context: getMappedDeviceConstant(DEVICE_DISCOVERY_STATUSES),
                width: 160,
                shown: true,
                priority: 4,
                groupable: true,
            },
            detectedVendor: {
                label: "Vendor",
                dataType: "enum",
                options: vendorList,
                width: 140,
                shown: true,
                priority: 5,
                groupable: true,
            },
            detectedHostName: {
                label: "Hostname",
                dataType: "text",
                width: 140,
                shown: true,
                priority: 6,
                groupable: false,
            },
            detectedPlatform: {
                label: "OS Platform",
                dataType: "text",
                width: 140,
                shown: true,
                priority: 7,
                groupable: true,
            },
            detectedSerialNumber: {
                label: "Serial",
                dataType: "text",
                width: 140,
                shown: true,
                priority: 8,
                groupable: true,
            },
            detectedSku: {
                label: "SKUs",
                dataType: "text",
                width: 140,
                shown: true,
                priority: 9,
                groupable: true,
            },
            lastSuccessfulBackupOn: {
                label: "Last Successful Backup On",
                dataType: "datetime",
                columnRendererComponent: DateColumn,
                shown: true,
                priority: 10,
                width: 120,
                groupable: false,
            },
            lastActivityOn: {
                label: "Last Activity On",
                dataType: "datetime",
                columnRendererComponent: DateColumn,
                width: 160,
                shown: true,
                priority: 11,
                groupable: false,
            },
            lastActivityType: {
                label: "Last Activity Type",
                dataType: "text",
                shown: true,
                priority: 12,
                groupable: true,
            },
            lastActivityError: {
                label: "Last Activity Error",
                dataType: "text",
                shown: true,
                priority: 13,
                groupable: true,
                width: 140,
            },
            description: {
                label: "Description",
                dataType: "text",
                width: 300,
                shown: false,
                priority: 14,
                groupable: false,
            },
            managementState: {
                label: "Management State",
                dataType: "enum",
                columnRendererComponent: ManagementStateColumn,
                options: getMappedDeviceConstant(DEVICE_MANAGEMENT_STATES),
                width: 140,
                shown: false,
                priority: 15,
                groupable: true,
            },
            discoveryLevel: {
                label: "Discovery Level",
                dataType: "enum",
                options: Object.entries(discoveryMap).map(([name, title]) => ({
                    value: Number(name),
                    title,
                })),
                columnRendererComponent: DiscoveryColumn,
                width: 140,
                shown: false,
                priority: 16,
                groupable: true,
            },
            environment: {
                label: "Environment",
                dataType: "enum",
                options: environmentList,
                columnRendererComponent: EnvironmentComponent,
                width: 140,
                shown: false,
                groupable: true,
                priority: 17,
            },
            controlType: {
                label: "Controller Type",
                dataType: "enum",
                columnRendererComponent: ControlTypeColumn,
                groupable: true,
                shown: false,
                width: 140,
                priority: 18,
                context: {
                    controlTypes: DeviceEntity.getControlTypeLabels(),
                },
                options: controlTypeList,
            },
            controllerName: {
                label: "Controller Name",
                dataType: "text",
                groupable: true,
                shown: false,
                width: 140,
                priority: 19,
            },
            connectionMethodDisplayName: {
                label: "Connection Method",
                dataType: "text",
                groupable: true,
                shown: false,
                width: 140,
                priority: 20,
            },
            sitePath: {
                label: "Site Path",
                dataType: "text",
                width: 160,
                shown: false,
                groupable: true,
                priority: 21,
                columnRendererComponent: SitePathColumn,
                context: {orgName: activeOrgName},
            },
            siteName: {
                label: "Site Name",
                dataType: "text",
                with: 140,
                shown: false,
                groupable: true,
                priority: 22,
            },
            siteCodeName: {
                label: "Site Code Name",
                dataType: "text",
                width: 160,
                shown: false,
                groupable: true,
                priority: 23,
            },
            siteLocationType: {
                label: "Site Type",
                dataType: "enum",
                options: sitesTypeList,
                width: 160,
                shown: false,
                priority: 24,
                groupable: true,
            },
            allowSiteAutoAssignment: {
                label: "Allow Site Auto Assignment",
                shown: false,
                dataType: "boolean",
                groupable: true,
                width: 120,
                columnRendererComponent: AllowSiteAutoAssignmentColumn,
                priority: 24,
            },
            licensed: {
                label: "Licensed",
                dataType: "boolean",
                columnRendererComponent: DeviceLicenseStatusComponent,
                options: getMappedDeviceConstant(LICENSE_STATUS),
                width: 140,
                shown: false,
                priority: 25,
                groupable: true,
            },
            credsName: {
                label: "Credential",
                dataType: "enum",
                groupable: true,
                shown: false,
                width: 150,
                priority: 26,
                options: credentialsList,
            },
            detectedOs: {
                label: "OS",
                dataType: "enum",
                width: 140,
                shown: false,
                priority: 27,
                groupable: true,
                options: osList,
            },
            detectedSoftwareVersion: {
                label: "OS Version",
                dataType: "text",
                width: 140,
                shown: false,
                priority: 28,
                groupable: true,
            },
            detectedUptime: {
                label: "Up Time",
                dataType: "duration",
                columnRendererComponent: UptimeColumn,
                width: 120,
                filterLabel: "Up Time (Days)",
                shown: false,
                priority: 29,
                groupable: false,
            },
            elementCount: {
                label: "Elements",
                dataType: "number",
                width: 110,
                shown: false,
                priority: 30,
                groupable: false,
            },
            totalCount: {
                label: "Switchports Total",
                width: 110,
                shown: false,
                // columnRendererComponent: DefaultNumericColumn,
                priority: 31,
                dataType: "number",
                path: "portSummary.totalCount",
            },
            useCount: {
                label: "Switchports Used",
                width: 110,
                shown: false,
                // columnRendererComponent: DefaultNumericColumn,
                priority: 32,
                dataType: "number",
                path: "portSummary.useCount",
            },
            freeCount: {
                label: "Switchports Free",
                width: 110,
                shown: false,
                // columnRendererComponent: DefaultNumericColumn,
                priority: 33,
                dataType: "number",
                path: "portSummary.freeCount",
            },
            usePercent: {
                label: "Switchports Used %",
                width: 110,
                shown: false,
                // columnRendererComponent: DefaultNumericColumn,
                priority: 34,
                dataType: "number",
                path: "portSummary.usePercent",
            },
            freePercent: {
                label: "Switchports Free %",
                width: 110,
                shown: false,
                // columnRendererComponent: DefaultNumericColumn,
                priority: 35,
                dataType: "number",
                path: "portSummary.freePercent",
            },
            zoneName: {
                label: "Zone",
                shown: false,
                groupable: true,
                width: 160,
                priority: 36,
            },
            licenseCount: {
                label: "License Count",
                dataType: "number",
                shown: false,
                groupable: true,
                width: 110,
                priority: 37,
            },
            configuredInfo: {
                label: "Configured Info",
                dataType: "text",
                width: 160,
                shown: false,
                groupable: true,
                priority: 38,
            },
            configuredType: {
                label: "Configured Type",
                dataType: "text",
                width: 120,
                shown: false,
                groupable: true,
                priority: 39,
            },
            configuredBy: {
                label: "Configured By",
                dataType: "text",
                width: 120,
                shown: false,
                groupable: true,
                priority: 40,
            },
            rebootedBy: {
                label: "Rebooted By",
                dataType: "text",
                width: 120,
                shown: false,
                groupable: false,
                priority: 41,
            },
            configuredOn: {
                label: "Configured On",
                dataType: "datetime",
                width: 160,
                columnRendererComponent: DateColumn,
                shown: false,
                groupable: false,
                priority: 42,
            },
            rebootedOn: {
                label: "Rebooted On",
                dataType: "datetime",
                width: 120,
                columnRendererComponent: DateColumn,
                shown: false,
                groupable: false,
                priority: 43,
            },
            updatedOn: {
                label: "Updated On",
                dataType: "datetime",
                columnRendererComponent: DateColumn,
                width: 160,
                shown: false,
                priority: 44,
                groupable: false,
            },
            updatedBy: {
                label: "Updated By",
                dataType: "text",
                width: 120,
                shown: false,
                priority: 45,
                groupable: true,
            },
            lastBackupStatus: {
                label: "Backup Status",
                dataType: "enum",
                shown: false,
                priority: 46,
                width: 120,
                groupable: true,
                options: Object.entries(backupLabelMap).map(([name, title]) => ({
                    value: name,
                    title,
                })),
            },
            lastBackupOn: {
                label: "Backup On",
                dataType: "datetime",
                columnRendererComponent: DateColumn,
                shown: false,
                priority: 47,
                width: 120,
                groupable: false,
            },
            lastBackedUpBy: {
                label: "Backup By",
                dataType: "text",
                shown: false,
                priority: 48,
                width: 120,
                groupable: true,
            },
            lastSuccessfulBackupName: {
                label: "Last Successful Backup Name",
                dataType: "text",
                shown: false,
                priority: 49,
                width: 120,
                groupable: true,
            },
            lastRestoredStatus: {
                label: "Restore Status",
                dataType: "enum",
                shown: false,
                priority: 50,
                width: 120,
                groupable: true,
                options: Object.entries(restoreLabelMap).map(([name, title]) => ({
                    value: name,
                    title,
                })),
            },
            lastRestoredProgress: {
                label: "Restore Progress",
                dataType: "text",
                shown: false,
                priority: 51,
                width: 120,
                groupable: true,
            },
            lastRestoredOn: {
                label: "Restored On",
                dataType: "datetime",
                columnRendererComponent: DateColumn,
                shown: false,
                priority: 52,
                width: 120,
                groupable: false,
            },
            lastRestoredBy: {
                label: "Restored By",
                dataType: "text",
                shown: false,
                priority: 53,
                width: 120,
                groupable: true,
            },
            lastRestoredBackupName: {
                label: "Restored Backup Name",
                dataType: "text",
                shown: false,
                priority: 54,
                width: 120,
                groupable: true,
            },
            lastRestoredBackupFrom: {
                label: "Restored Backup From",
                dataType: "datetime",
                shown: false,
                priority: 55,
                width: 120,
                groupable: false,
            },
            lastRestoredBackupOn: {
                label: "Restored Backup From",
                dataType: "datetime",
                columnRendererComponent: DateColumn,
                shown: false,
                priority: 56,
                width: 120,
                groupable: false,
            },
            lastActivityBy: {
                label: "Last Activity By",
                dataType: "text",
                width: 140,
                shown: false,
                priority: 57,
                groupable: true,
            },
            accessStatus: {
                label: "Access Status",
                dataType: "enum",
                shown: false,
                priority: 58,
                groupable: true,
                columnRendererComponent: AccessStatusColumn,
                options: ACCESS_STATUS_LABELS,
                width: 140,
            },
            cmdbCiId: {
                label: "CMDB CI ID",
                dataType: "text",
                shown: false,
                priority: 59,
                groupable: false,
                width: 140,
            },
            cmdbCiName: {
                label: "CMDB CI Name",
                dataType: "text",
                shown: false,
                priority: 60,
                groupable: false,
                width: 140,
            },
            cmdbClassName: {
                label: "CMDB Class Name",
                dataType: "text",
                shown: false,
                priority: 61,
                groupable: true,
                width: 140,
            },
            cmdbSyncStatus: {
                label: "CMDB Sync Status",
                dataType: "enum",
                shown: false,
                priority: 62,
                groupable: true,
                options: cmdbSyncStatusList,
                columnRendererComponent: CmdbSyncStatusColumn,
                context: {
                    cmdbSyncStatusOptions: cmdbSyncStatusMap,
                },
                width: 140,
            },
            lastCmdbSyncBy: {
                label: "Last CMDB Sync By",
                dataType: "text",
                shown: false,
                priority: 63,
                groupable: false,
                width: 140,
            },
            lastCmdbSyncError: {
                label: "Last CMDB Sync Error",
                dataType: "text",
                shown: false,
                priority: 64,
                groupable: true,
                width: 140,
            },
            lastCmdbSyncOn: {
                label: "Last CMDB Sync On",
                dataType: "datetime",
                shown: false,
                priority: 65,
                groupable: false,
                columnRendererComponent: DateColumn,
                width: 140,
            },
            vrf: {
                label: "VRF",
                dataType: "text",
                width: 140,
                shown: false,
                priority: 66,
                groupable: false,
            },
            haPairName: {
                label: "HA Group",
                dataType: "text",
                width: 140,
                shown: false,
                priority: 67,
                groupable: true,
            },
            useIssu: {
                label: "Non-disruptive",
                dataType: "boolean",
                options: getMappedDeviceConstant(DISRUPTIVE_STATUS),
                width: 140,
                shown: false,
                priority: 68,
                groupable: true,
            },
            discoveredOn: {
                label: "Discovered On",
                dataType: "datetime",
                columnRendererComponent: DateColumn,
                width: 160,
                shown: false,
                priority: 69,
                groupable: false,
            },
            discoveredBy: {
                label: "Discovered By",
                dataType: "text",
                width: 120,
                shown: false,
                priority: 70,
                groupable: true,
            },
        };

        if (Steve?.licenseRestriction?.checkLicense("CDA")) {
            Object.assign(legacyDeviceColumnConfig, {
                latestHaveIChangedLastChecked: {
                    label: "Captured On",
                    dataType: "datetime",
                    columnRendererComponent: DateColumn,
                    width: 140,
                    shown: false,
                    groupable: false,
                    priority: 71,
                },
                latestHaveIChanged: {
                    label: "Captured Status",
                    dataType: "enum",
                    columnRendererComponent: DeviceCaptureStatusColumn,
                    options: getMappedDeviceConstant(DEVICE_CAPTURE_STATUSES),
                    width: 140,
                    shown: false,
                    groupable: true,
                    priority: 72,
                },
                capturedBy: {
                    label: "Captured By",
                    dataType: "text",
                    width: 120,
                    shown: false,
                    groupable: true,
                    priority: 73,
                },
                auditCriticalViolationCount: {
                    label: "Critical Violations",
                    dataType: "number",
                    priority: 74,
                    width: 130,
                    groupable: false,
                    shown: false,
                },
                auditMajorViolationCount: {
                    label: "Major Violations",
                    dataType: "number",
                    priority: 75,
                    width: 120,
                    groupable: false,
                    shown: false,
                },
                auditMinorViolationCount: {
                    label: "Minor Violations",
                    dataType: "number",
                    priority: 76,
                    width: 120,
                    groupable: false,
                    shown: false,
                },
                auditInfoViolationCount: {
                    label: "Informational Violations",
                    dataType: "number",
                    priority: 77,
                    width: 175,
                    groupable: false,
                    shown: false,
                },
                auditViolationCount: {
                    label: "Total Violations",
                    dataType: "number",
                    priority: 78,
                    width: 120,
                    groupable: false,
                    shown: false,
                },
                auditedOn: {
                    label: "Audited On",
                    dataType: "datetime",
                    columnRendererComponent: DateColumn,
                    width: 160,
                    shown: false,
                    priority: 79,
                },
                auditedBy: {
                    label: "Audited By",
                    dataType: "text",
                    width: 160,
                    shown: false,
                    groupable: false,
                    priority: 80,
                },
                auditStatus: {
                    label: "Audit Status",
                    dataType: "enum",
                    options: getMappedDeviceConstant(AUDIT_STATUSES),
                    columnRendererComponent: AuditStatusColumn,
                    width: 160,
                    shown: false,
                    groupable: false,
                    priority: 81,
                },
                lastAuditPolicy: {
                    label: "Audit Policy",
                    dataType: "text",
                    width: 140,
                    shown: false,
                    groupable: false,
                    priority: 82,
                },
            });
        }

        if (Steve?.licenseRestriction?.checkLicense("OSM")) {
            Object.assign(legacyDeviceColumnConfig, {
                fileServerName: {
                    label: "File Server",
                    dataType: "enum",
                    options: serverList,
                    width: 110,
                    shown: false,
                    priority: 83,
                    groupable: true,
                },
                osUpgradeStatus: {
                    label: "OSM Action Status",
                    dataType: "enum",
                    shown: false,
                    priority: 84,
                    columnRendererComponent: PlanStatusColumn,
                    options: _getOsPlanDataForAdvancedFilter(
                        DeviceEntity.getOsUpgradeStatusValues()
                    ),
                    context: {
                        _planStatusList: DeviceEntity.getOsUpgradeStatusValues(),
                    },
                },
                osUpgradedBy: {
                    label: "OSM Action By",
                    dataType: "text",
                    shown: false,
                    groupable: true,
                    priority: 85,
                },
                osUpgradedOn: {
                    label: "OSM Action On",
                    dataType: "datetime",
                    shown: false,
                    groupable: false,
                    priority: 86,
                    columnRendererComponent: DateColumn,
                },
                osUpgradeError: {
                    label: "OSM Action Error",
                    dataType: "text",
                    shown: false,
                    groupable: false,
                    priority: 87,
                },
                osUpgradePlanId: {
                    label: "OS Plan ID",
                    dataType: "text",
                    shown: false,
                    groupable: false,
                    priority: 88,
                },
                osUpgradePlanName: {
                    label: "OS Plan Name",
                    dataType: "text",
                    shown: false,
                    groupable: true,
                    priority: 89,
                },
                osUpgradePlanAction: {
                    label: "OS Plan Action",
                    dataType: "text",
                    shown: false,
                    groupable: true,
                    priority: 90,
                },
            });
        }

        // Tags display the device's tag names; the id-based "Tags" select is filter-only.
        Object.assign(legacyDeviceColumnConfig, {
            tagNames: {
                label: "Tags",
                filterLabel: "Tag Name",
                dataType: "array",
                columnRendererComponent: ArrayColumn,
                width: 160,
                shown: false,
                groupable: false,
                priority: 91,
            },
        });

        return legacyDeviceColumnConfig;
    } catch (error) {
        console.error("Error loading legacy column config:", error);
        return {};
    }
};

export const deviceManagerDevicesColumnConfig = {
    getColumnConfig: async () => {
        const activeOrgId = _.get(Steve, "userSession.activeOrganization.id");
        const legacyColumnConfig = await getLegacyColumnConfig();

        let d3ColumnConfig = {};

        try {
            d3ColumnConfig = await DeviceEntity.loadDisplayProps(activeOrgId);
            d3ColumnConfig = _normalizeD3ColumnConfig(d3ColumnConfig);

            ["tags", "tagIds", "addTagIds", "removeTagIds"].forEach(
                (key) => delete d3ColumnConfig[key]
            );

            // Normalize d3ColumnConfig to match the columnConfig format
            for (const key of Object.keys(d3ColumnConfig)) {
                // d3 options must be converted to old format
                if (d3ColumnConfig[key].options) {
                    const rawOptions = d3ColumnConfig[key].options;
                    if (Array.isArray(rawOptions)) {
                        // Backend returned an array — indices are meaningless, the string value IS the DB value
                        d3ColumnConfig[key].options = rawOptions.map((title) => ({
                            value: title,
                            title,
                        }));
                    } else {
                        // Backend returned an object — keys are meaningful DB values
                        d3ColumnConfig[key].options = Object.entries(rawOptions).map(
                            ([name, title]) => ({value: name, title})
                        );
                    }
                }

                // Make all d3 columns hidden by default
                d3ColumnConfig[key].shown = false;

                // Set width based on label length
                if (!d3ColumnConfig[key].width) {
                    const displayNameLength = d3ColumnConfig[key].label?.length || 0;
                    const minWidth = 140;
                    const maxWidth = 300;

                    d3ColumnConfig[key].width =
                        displayNameLength > 12
                            ? Math.min(displayNameLength * 7 + 40, maxWidth)
                            : minWidth;
                }

                // Set column renderer component based on data type
                switch (d3ColumnConfig[key].dataType) {
                    case "unixTime":
                        d3ColumnConfig[key].dataType = "datetime";
                        d3ColumnConfig[key].columnRendererComponent = DateColumn;
                        break;
                    case "boolean":
                        d3ColumnConfig[key].columnRendererComponent = BooleanColumn;
                        break;
                    case "duration":
                        d3ColumnConfig[key].columnRendererComponent = UptimeColumn;
                        break;
                    case "array":
                        d3ColumnConfig[key].dataType = "string";
                        d3ColumnConfig[key].columnRendererComponent = ArrayColumn;
                        break;
                    case "string":
                        d3ColumnConfig[key].dataType = "text";
                        break;
                    case "object":
                        d3ColumnConfig[key].dataType = "text";
                        break;
                    case "number":
                    case "enum":
                        // No action needed
                        break;
                    default:
                        console.warn("Skipping unknown type: " + d3ColumnConfig[key].dataType);
                        break;
                }
            }
        } catch (err) {
            console.warn("Failed to load d3 props for device columns", err);
            d3ColumnConfig = {};
        }

        // Merge legacyColumnConfig into d3ColumnConfig
        return Object.assign({}, d3ColumnConfig, legacyColumnConfig);
    },
};
